/**
 * PHASE 9 — which one-time unlocks this viewer holds (master plan §9).
 *
 * Poker Pro includes Blackjack Pro and Cribbage Pro, so a Pro account is answered from
 * `/api/me` alone and nothing more is fetched. A free account asks the Worker what it
 * bought (`GET /api/billing/unlocks`); a signed-out visitor holds nothing. A failed
 * answer reads as "holds nothing beyond the plan" — the conservative answer, the one
 * that never prints a paid number to somebody who has not paid — and is NOT kept, so
 * the next screen asks again rather than locking a paying owner out for the session.
 *
 * `loading` is true until the answer is known: while Clerk is still loading, while
 * `/api/me` is on its way, and while the unlocks are. A screen shows neither a number
 * nor a lock while it is — no lock flashing at an owner, no number at anyone else. If
 * `/api/me` fails, loading ends and the answer is "nothing".
 *
 * The answer is kept for the page's life, per account, so the room and its drills do
 * not each ask. `refreshUnlocks()` drops it and has every mounted screen ask again — the
 * account page calls it on the way back from a paid checkout, Pricing after a 409.
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { POKER_PRO_INCLUDES, type UnlockId } from "@config/plans";
import { api } from "./api";
import { useMeContext } from "./MeContext";

const cache = new Map<string, Promise<UnlockId[]>>();
const listeners = new Set<() => void>();
export function refreshUnlocks(): void {
  cache.clear();
  for (const l of listeners) l();
}

export interface UnlockState {
  /** Still being asked. Show neither the number nor the lock. */
  loading: boolean;
  has: (id: UnlockId) => boolean;
  /** True when the account is on Poker Pro (so both are included, not bought). */
  viaPlan: boolean;
}

export function useUnlocks(): UnlockState {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { me, error: meError } = useMeContext();
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  /* `me` can outlive a sign-out by a render: a plan is only believed while signed in. */
  const pro = !!isSignedIn && me?.plan.id === "pro";
  const who = isSignedIn ? me?.user.id ?? null : null;
  const [bought, setBought] = useState<{ who: string; ids: UnlockId[] } | null>(null);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setEpoch((n) => n + 1);
    listeners.add(bump);
    return () => { listeners.delete(bump); };
  }, []);

  useEffect(() => {
    if (!isSignedIn || !who || pro) return;
    let on = true;
    let p = cache.get(who);
    if (!p) {
      setBought(null);                       // a fresh question: loading until it is answered
      const ask = api.unlocks(tokenRef.current).then((r) => r.unlocks);
      /* Forget only THIS failed question — `refreshUnlocks()` may have put a newer one in
         the cache while this one was failing, and that one must stand. */
      const mine: Promise<UnlockId[]> = ask.catch(() => { if (cache.get(who) === mine) cache.delete(who); return [] as UnlockId[]; });
      p = mine;
      cache.set(who, p);
    }
    p.then((ids) => { if (on) setBought({ who, ids }); });
    return () => { on = false; };
  }, [isSignedIn, who, pro, epoch]);

  const ids: readonly UnlockId[] = pro ? POKER_PRO_INCLUDES : bought && bought.who === who ? bought.ids : [];
  const loading = !isLoaded
    || (!!isSignedIn && !pro && !meError && (!me || !bought || bought.who !== who));
  return {
    loading,
    has: (id) => !loading && ids.includes(id),
    viaPlan: pro,
  };
}
